import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { showToast } from '@/components/Layout/Toast';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import Alert from '@/components/UI/Alert';
import Button from '@/components/UI/Button';
import FormInput from '@/components/UI/FormInput';
import Card from '@/components/UI/Card';
import { useAuth } from '@/context/AuthContext';
import { trpc } from '@/api/trpc';
import { useMutation } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';

const Enable2FA: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, refreshUser, requiresTwoFactorSetup, sessionPolicy } = useAuth();
    // We're using the global spinner, so we don't need a local loading state
    const [error, setError] = useState<string | null>(null);
    const [qrCode, setQrCode] = useState<string>('');
    const [authCode, setAuthCode] = useState<string>('');
    const [isEnabled, setIsEnabled] = useState(false);

    // Check if 2FA is already enabled for this user
    const is2faAlreadyEnabled = Boolean(user?.['2faEnabled']);
    const redirectUrl = new URLSearchParams(location.search).get('redirect') || '/user/portfolios';
    const canManage2fa = sessionPolicy.canManage2fa;

    // Set up the 2FA setup mutation
    const setup2faMutation = useMutation({
        ...trpc.user.setup2fa.mutationOptions(),
        onSuccess: (data) => {
            if (data?.qrCode) {
                setQrCode(data.qrCode);
                setError(null);
            }

            hideSpinner();
        },
        onError: (error) => {
            console.error('Error setting up 2FA:', error);
            setError('Failed to set up two-factor authentication. Please try again later.');
            showToast('Failed to set up two-factor authentication.', 'error');
            hideSpinner();
        }
    });

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    // Trigger 2FA setup when component mounts
    useEffect(() => {
        // If 2FA is already enabled, don't try to set it up again
        if (!canManage2fa) {
            hideSpinner();
            return;
        }

        if (is2faAlreadyEnabled) {
            hideSpinner();
            return;
        }

        // Trigger the setup2fa mutation
        setup2faMutation.mutate();
        // Remove setup2faMutation from dependencies to prevent infinite loop
    }, [canManage2fa, is2faAlreadyEnabled]);

    // Set up the enable 2FA mutation using useTrpcMutation helper
    const enable2faMutation = useTrpcMutation(
        trpc.user.enable2fa.mutationOptions({
            onSuccess: async () => {
                await refreshUser();
                setIsEnabled(true);
                setAuthCode('');
            }
        }),
        {
            successMessage: '2FA enabled successfully',
            fallbackErrorMessage: 'Failed to enable two-factor authentication'
        }
    );

    const isEnabling = enable2faMutation.isPending;

    const handleEnable2FA = () => {
        if (!canManage2fa) {
            showToast('Two-factor authentication setup is unavailable during a support session.', 'warning');
            return;
        }

        if (authCode.length !== 6) {
            showToast('Please enter a valid 6-digit authentication code', 'warning');
            return;
        }

        try {
            enable2faMutation.mutate({ token2fa: authCode });
        } catch (err) {
            console.error('Error enabling 2FA:', err);
            setError('Failed to enable two-factor authentication. Please check your code and try again.');
            showToast('Failed to enable two-factor authentication.', 'error');
        }
    };

    // We don't need to show a local loading spinner anymore since we're using the global one

    if (!canManage2fa) {
        return (
            <UserLayout title="Enable Two-Factor Authentication - Project 314">
                <SupportRestrictedFeatureCard
                    description="2FA enrollment is disabled during a read-only support session so an admin cannot alter the customer’s account security configuration."
                    title="2FA Setup Is Unavailable"
                />
            </UserLayout>
        );
    }

    if (error) {
        return (
            <UserLayout title="Enable Two-Factor Authentication - Project 314">
                <Alert variant="danger">{error}</Alert>
            </UserLayout>
        );
    }

    return (
        <UserLayout title="Enable Two-Factor Authentication - Project 314">
            <div className="w-full px-4">
                <div className="my-3">
                    <h3 className="text-white mb-0">Enable Two-Factor Authentication</h3>
                </div>

                {requiresTwoFactorSetup && (
                    <Card className="mb-4">
                        <div className="p-3">
                            <h5 className="mt-2 mb-2 font-semibold">Step 2 of 2: Secure your account</h5>
                            <p>
                                Your account has been created. Finish setting up two-factor authentication before accessing the rest of
                                Project 314.
                            </p>
                        </div>
                    </Card>
                )}

                {isEnabled ? (
                    <Card>
                        <div className="p-4 text-center">
                            <h4 className="text-profit-green mb-5 font-semibold">Two-Factor Authentication Enabled Successfully!</h4>
                            <p>
                                Your account is now protected with two-factor authentication. You will need to enter a verification code
                                from your authenticator app when logging in.
                            </p>
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-5 rounded">
                                <strong>Important:</strong> Make sure to keep your authenticator app accessible. If you lose access to your
                                authenticator app, you may lose access to your account.
                            </div>
                            <div className="mt-4">
                                <Button variant="primary" onClick={() => navigate(redirectUrl, { replace: true })}>
                                    Continue
                                </Button>
                            </div>
                        </div>
                    </Card>
                ) : is2faAlreadyEnabled ? (
                    <Card>
                        <div className="p-4 text-center">
                            <h4 className="text-profit-green mb-5 font-semibold">Two-Factor Authentication is Already Enabled</h4>
                            <p>
                                Your account is already protected with two-factor authentication. You will need to enter a verification code
                                from your authenticator app when logging in.
                            </p>
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-5 rounded">
                                <strong>Important:</strong> Make sure to keep your authenticator app accessible. If you lose access to your
                                authenticator app, you may lose access to your account.
                            </div>
                        </div>
                    </Card>
                ) : (
                    <>
                        <Card className="mb-4">
                            <div className="p-3">
                                <h5 className="mt-4 mb-2 font-semibold">What is Two-Factor Authentication?</h5>
                                <p>
                                    Two-Factor Authentication (2FA) adds an extra layer of security to your account. In addition to your
                                    password, you&apos;ll need a code from your authenticator app to log in.
                                </p>
                                <h5 className="mt-4 mb-2 font-semibold">How to set up 2FA:</h5>
                                <ol className="list-decimal pl-5">
                                    <li className="mb-2">
                                        Download an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator
                                    </li>
                                    <li className="mb-2">Scan the QR code below with your authenticator app</li>
                                    <li className="mb-2">Enter the 6-digit code shown in your authenticator app</li>
                                    <li className="mb-2">Click &quot;Enable 2FA&quot; to complete the setup</li>
                                </ol>
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4 mt-1 text-center">
                                <div className="mb-4" id="2faQrCodeDiv">
                                    <h5 className="mb-3 font-semibold">Scan the QR code below with your authenticator app</h5>
                                    {qrCode && (
                                        <div className="flex justify-center my-5">
                                            <img
                                                alt="QR Code for 2FA setup"
                                                className="max-w-[250px] h-auto border border-zinc-300 p-2.5 bg-white rounded shadow-sm"
                                                src={qrCode}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3">
                                    <p>Enter the code from your authenticator app:</p>
                                    <div className="max-w-[300px] mx-auto">
                                        <FormInput
                                            className="text-center"
                                            id="2faCode"
                                            maxLength={6}
                                            placeholder="Enter the 6-digit code"
                                            value={authCode}
                                            onChange={(e) => setAuthCode(e.target.value.replace(/[^0-9]/g, ''))}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && authCode.length >= 6) {
                                                    handleEnable2FA();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <Button
                                        disabled={isEnabling || authCode.length !== 6}
                                        isLoading={isEnabling}
                                        variant="primary"
                                        onClick={handleEnable2FA}
                                    >
                                        Enable 2FA
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </UserLayout>
    );
};

export default Enable2FA;
